import React, {
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

type HistoryEntry = {
  type: "system" | "command" | "output" | "error";
  text: string;
};

type CommandHandler = (args: string[]) => string | void;

function TerminalPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const terminalBodyRef = useRef<HTMLDivElement | null>(null);

  const [input, setInput] = useState<string>("");
  const [history, setHistory] = useState<HistoryEntry[]>([
    {
      type: "system",
      text: "Willkommen im Nerd-Terminal.",
    },
    {
      type: "system",
      text: 'Tippe "help", um alle Befehle zu sehen.',
    },
  ]);

  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  const pushHistory = (entry: HistoryEntry): void => {
    setHistory((prev) => [...prev, entry]);
  };

  const commands = useMemo<Record<string, CommandHandler>>(() => {
    return {
      help: () => {
        return [
          "Verfügbare Commands:",
          "",
          "help                - zeigt alle Befehle",
          "whoami              - zeigt kurze Info",
          "about               - Infos über mich",
          "skills              - zeigt meine Skills",
          "projects            - zeigt meine Projekte",
          "contact             - zeigt Kontaktinfos",
          "socials             - zeigt Social Links",
          "email               - zeigt E-Mail",
          "github              - öffnet GitHub",
          "linkedin            - öffnet LinkedIn",
          "spotify             - öffnet die Spotify-Unterseite",
          "date                - zeigt Datum und Uhrzeit",
          "pwd                 - zeigt aktuellen virtuellen Pfad",
          "clear               - leert das Terminal",
          "",
          "Navigation:",
          "go home             - navigiert zur Startseite",
          "go about            - navigiert zu About",
          "go projects         - navigiert zu Projects",
          "go terminal         - navigiert zu Terminal",
          "go spotify          - navigiert zu Spotify",
          "go contact          - navigiert zu Contact",
        ].join("\n");
      },

      whoami: () => {
        return "Ich bin Dein Name, Frontend Developer, React-Bauer und Tech Nerd.";
      },

      about: () => {
        return [
          "Über mich:",
          "Ich interessiere mich für Programmierung, moderne Webentwicklung,",
          "cleane UIs, TypeScript und stylische Frontends mit Nerd-Vibe.",
        ].join("\n");
      },

      skills: () => {
        return [
          "Skills:",
          "- React",
          "- TypeScript",
          "- JavaScript",
          "- HTML5",
          "- CSS3",
          "- React Router",
          "- Git",
          "- UI/UX",
        ].join("\n");
      },

      projects: () => {
        return [
          "Projekte:",
          "1. Portfolio Website",
          "2. Dashboard UI",
          "3. Spotify Page",
          "4. Code Playground",
        ].join("\n");
      },

      contact: () => {
        return [
          "Kontakt:",
          "E-Mail: deinname@email.com",
          "GitHub: github.com/deinusername",
          "LinkedIn: linkedin.com/in/deinprofil",
        ].join("\n");
      },

      socials: () => {
        return [
          "Socials:",
          "github    -> öffnet GitHub",
          "linkedin  -> öffnet LinkedIn",
          "spotify   -> öffnet Spotify-Unterseite",
          "email     -> zeigt E-Mail",
        ].join("\n");
      },

      email: () => {
        return "deinname@email.com";
      },

      github: () => {
        window.open(
          "https://github.com/deinusername",
          "_blank",
          "noopener,noreferrer",
        );
        return "Öffne GitHub...";
      },

      linkedin: () => {
        window.open(
          "https://linkedin.com/in/deinprofil",
          "_blank",
          "noopener,noreferrer",
        );
        return "Öffne LinkedIn...";
      },

      spotify: () => {
        navigate("/spotify");
        return "Navigiere zu Spotify...";
      },

      date: () => {
        return new Date().toLocaleString("de-DE");
      },

      pwd: () => {
        return "/portfolio/terminal";
      },

      go: (args: string[]) => {
        const target = args[0]?.toLowerCase();

        switch (target) {
          case "home":
            navigate("/");
            return "Navigiere zu Home...";

          case "about":
            navigate("/about");
            return "Navigiere zu About...";

          case "projects":
            navigate("/projects");
            return "Navigiere zu Projects...";

          case "terminal":
            navigate("/terminal");
            return "Du bist bereits im Terminal.";

          case "spotify":
            navigate("/spotify");
            return "Navigiere zu Spotify...";

          case "contact":
            navigate("/contact");
            return "Navigiere zu Contact...";

          default:
            return "Ungültiges Ziel. Nutze: go home | go about | go projects | go terminal | go spotify | go contact";
        }
      },

      clear: () => {
        setHistory([]);
        return "";
      },
    };
  }, [navigate]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (terminalBodyRef.current) {
      terminalBodyRef.current.scrollTop = terminalBodyRef.current.scrollHeight;
    }
  }, [history]);

  const runCommand = (rawValue: string): void => {
    const trimmed = rawValue.trim();

    if (!trimmed) {
      return;
    }

    pushHistory({
      type: "command",
      text: `visitor@portfolio:~$ ${trimmed}`,
    });

    setCommandHistory((prev) => [...prev, trimmed]);
    setHistoryIndex(-1);

    const parts = trimmed.split(/\s+/);
    const commandName = parts[0].toLowerCase();
    const args = parts.slice(1);

    const command = commands[commandName];

    if (!command) {
      pushHistory({
        type: "error",
        text: `Command not found: ${commandName}. Tippe "help" für verfügbare Befehle.`,
      });
      return;
    }

    const result = command(args);

    if (typeof result === "string" && result.trim() !== "") {
      pushHistory({
        type: "output",
        text: result,
      });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      runCommand(input);
      setInput("");
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();

      if (commandHistory.length === 0) {
        return;
      }

      const nextIndex =
        historyIndex === -1
          ? commandHistory.length - 1
          : Math.max(0, historyIndex - 1);

      setHistoryIndex(nextIndex);
      setInput(commandHistory[nextIndex]);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();

      if (commandHistory.length === 0 || historyIndex === -1) {
        return;
      }

      const nextIndex = historyIndex + 1;

      if (nextIndex >= commandHistory.length) {
        setHistoryIndex(-1);
        setInput("");
        return;
      }

      setHistoryIndex(nextIndex);
      setInput(commandHistory[nextIndex]);
    }
  };

  return (
    <section className="content-section">
      <h1 className="section-title">Terminal</h1>

      <div className="glass-card terminal-info">
        <p>
          Tippe <strong>help</strong>, um alle Befehle zu sehen.
        </p>
        <p>
          Mit <strong>go home</strong>, <strong>go about</strong>,{" "}
          <strong>go projects</strong> oder <strong>go spotify</strong> kannst
          du direkt navigieren.
        </p>
      </div>

      <div
        className="custom-terminal"
        onClick={() => inputRef.current?.focus()}
      >
        <div className="custom-terminal-topbar">
          <span className="dot dot-red" />
          <span className="dot dot-yellow" />
          <span className="dot dot-green" />
          <span className="terminal-title">visitor@portfolio: ~/terminal</span>
        </div>

        <div className="custom-terminal-body" ref={terminalBodyRef}>
          {history.map((entry, index) => (
            <pre
              key={`${entry.type}-${index}`}
              className={`terminal-line-output ${entry.type}`}
            >
              {entry.text}
            </pre>
          ))}

          <div className="terminal-input-row">
            <span className="terminal-prompt">visitor@portfolio:~$</span>
            <input
              ref={inputRef}
              className="terminal-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default TerminalPage;
